import { useEffect, useMemo, useState } from "react";
import { Filter, Search, Download } from "lucide-react";
import { API_BASE } from "@/lib/utils";

// const API_BASE = typeof window !== "undefined" ? "" : "http://localhost:8001";

export default function Audit() {
  const [query, setQuery] = useState("");
  const [actorFilter, setActorFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams();
    const q = query.trim();
    if (q) params.set("q", q);
    if (actorFilter !== "all") params.set("actor", actorFilter);
    if (actionFilter !== "all") params.set("action", actionFilter);
    const qs = params.toString();
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/audit${qs ? `?${qs}` : ""}`);
        if (!res.ok) throw new Error(`Failed to load audit log (${res.status})`);
        const data = await res.json();
        if (!cancelled) setItems(data.items || []);
      } catch (err) {
        if (!cancelled) setError(err?.message || "Failed to load audit log");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [query, actorFilter, actionFilter]);

  const actorOptions = useMemo(
    () => ["all", ...new Set(items.map((a) => String(a?.actor || "system").toLowerCase()))],
    [items],
  );
  const actionOptions = useMemo(
    () => ["all", ...new Set(items.map((a) => String(a?.action || "unknown").toLowerCase()))],
    [items],
  );

  const filtered = useMemo(() => {
    return items.filter((a) => withinWindow(a?.ts, dateFilter));
  }, [items, dateFilter]);

  const exportAudit = () => {
    const rows = [
      ["timestamp", "actor", "action", "target"],
      ...filtered.map((a) => [a?.ts || "", a?.actor || "system", a?.action || "unknown", a?.target || ""]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replaceAll("\"", "\"\"")}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1500px] mx-auto" data-testid="audit-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-overline">Workspace · audit log</div>
          <h1 className="font-heading text-4xl mt-2 tracking-tight font-medium">Audit log</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-2">Server-filtered for search/actor/action; date window applied client-side.</p>
        </div>
        <button type="button" className="btn-secondary" onClick={exportAudit}><Download size={14} /> Export</button>
      </div>

      <div className="mt-6 md:mt-10 flex items-center gap-2 border border-[var(--border-default)] bg-[var(--bg-surface)] p-2 flex-wrap">
        <Search size={14} className="ml-2 text-[var(--text-tertiary)]" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search actor, action, target…" className="bg-transparent flex-1 min-w-[120px] px-2 py-1.5 text-sm focus:outline-none" />
        <label className="inline-flex items-center gap-2 text-xs font-mono text-[var(--text-secondary)] px-2">
          <Filter size={12} /> Actor
          <select value={actorFilter} onChange={(e) => setActorFilter(e.target.value)} className="bg-[var(--bg-elev)] border border-[var(--border-default)] px-2 py-1">
            {actorOptions.map((a) => (
              <option key={a} value={a}>{a === "all" ? "All" : a}</option>
            ))}
          </select>
        </label>
        <label className="inline-flex items-center gap-2 text-xs font-mono text-[var(--text-secondary)] px-2">
          Action type
          <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} className="bg-[var(--bg-elev)] border border-[var(--border-default)] px-2 py-1">
            {actionOptions.map((a) => (
              <option key={a} value={a}>{a === "all" ? "All" : a}</option>
            ))}
          </select>
        </label>
        <label className="inline-flex items-center gap-2 text-xs font-mono text-[var(--text-secondary)] px-2">
          Date range
          <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="bg-[var(--bg-elev)] border border-[var(--border-default)] px-2 py-1">
            <option value="all">All</option>
            <option value="24h">Last 24h</option>
            <option value="7d">Last 7d</option>
            <option value="30d">Last 30d</option>
          </select>
        </label>
      </div>

      <div className="mt-6 surface overflow-x-auto">
        <table className="w-full text-sm min-w-[550px]">
          <thead className="border-b border-[var(--border-default)] text-[var(--text-tertiary)] font-mono text-[10px] uppercase tracking-widest">
            <tr>
              <th className="p-4 text-left">Timestamp</th>
              <th className="p-4 text-left">Actor</th>
              <th className="p-4 text-left">Action</th>
              <th className="p-4 text-left">Target</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a, idx) => (
              <tr key={`${a?.id ?? "row"}-${a?.ts ?? idx}-${idx}`} className="border-b border-[var(--border-default)] hover:bg-[var(--bg-elev)]">
                <td className="p-4 font-mono text-xs text-[var(--text-secondary)]">{a?.ts || "—"}</td>
                <td className="p-4 font-mono text-xs">{a?.actor || "system"}</td>
                <td className="p-4">
                  <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-[var(--border-default)] text-[var(--text-primary)]">{a?.action || "unknown"}</span>
                </td>
                <td className="p-4 text-[var(--text-secondary)]">{a?.target || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && !error && filtered.length === 0 && <div className="p-4 text-sm text-[var(--text-secondary)]">No audit entries found.</div>}
      </div>
      {error && <div className="mt-6 text-sm text-[var(--error)]">{error}</div>}
    </div>
  );
}

function withinWindow(ts, windowKey) {
  if (windowKey === "all") return true;
  const raw = String(ts || "").replace(" UTC", "Z").replace(" ", "T");
  const parsed = Date.parse(raw);
  if (Number.isNaN(parsed)) return false;
  const ageMs = Date.now() - parsed;
  if (windowKey === "24h") return ageMs <= 24 * 60 * 60 * 1000;
  if (windowKey === "7d") return ageMs <= 7 * 24 * 60 * 60 * 1000;
  if (windowKey === "30d") return ageMs <= 30 * 24 * 60 * 60 * 1000;
  return true;
}
